import { DomainDto } from '../../../../dtos/domain'
import { NotificationAutoSuggestFields } from '../../../../enums/notification-auto-suggest-fields.enum'
import { INotificationFieldSuggestions } from '../../../../interfaces/notification-field-suggestions'
import { extract } from '../../../../_utils/object'
import { IEnhancedNotificationFieldSuggestions, SuggestedFieldsForm } from './types'

/**
 * Determines if a field can be shown based on domain configuration
 */
export function canShowField(domain: DomainDto, field: keyof INotificationFieldSuggestions): boolean {
    const allowedFields = domain.notificationAutoSuggestFields
    const key = (field === 'imageUrl' ? 'image_url' : field) as NotificationAutoSuggestFields
    return allowedFields === null ? true : allowedFields?.includes(key) ?? false
}

/**
 * Sorts keywords alphabetically for display
 */
export function sortKeywordsForDisplay(keywords: string[] | null): string[] {
    if (!keywords || !keywords.length) return []

    // First, sort alphabetically
    const sorted = [...keywords].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))

    // For flexbox row-major layout to appear column-major, we need to reorder items
    // If we have items [A,B,C,D,E,F] and want 3 columns:
    // Column layout: A D    Row layout needed: A B C
    //                B E                       D E F
    //                C F
    // So we need to arrange as: [A,B,C,D,E,F] -> [A,D,B,E,C,F]

    const columns = 3
    const rows = Math.ceil(sorted.length / columns)
    const reordered: string[] = []

    // Fill by rows, but pick items from column positions
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < columns; col++) {
            const index = col * rows + row
            if (index < sorted.length) {
                reordered.push(sorted[index])
            }
        }
    }

    return reordered
}

/**
 * Extracts form selections for page analyzer results
 */
export function extractFormSelections(
    values: SuggestedFieldsForm,
    variant: IEnhancedNotificationFieldSuggestions,
    domain: DomainDto,
    selectedKeywords?: string[] | null,
): Partial<INotificationFieldSuggestions> {
    let selections: Partial<INotificationFieldSuggestions> = {}

    if (values.title && canShowField(domain, 'title')) {
        selections.title = variant.title
    }
    if (values.body && canShowField(domain, 'body')) {
        selections.body = variant.body
    }
    if (values.imageUrl && canShowField(domain, 'imageUrl')) {
        if (variant.compositeImage) {
            selections.compositeImage = variant.compositeImage
            selections.imageUrl = variant.imageUrl
        } else {
            const suggestedImage = variant.compositeImage ? extract(variant.compositeImage, 'url') : variant.imageUrl
            selections.imageUrl = suggestedImage
        }
    }
    if ((values.keywords || selectedKeywords?.length) && canShowField(domain, 'keywords')) {
        // Always use selectedKeywords as the source of truth since it reflects
        // individual keyword selections/deselections by the user
        selections.keywords = selectedKeywords?.map((kw) => kw.toLowerCase()) ?? null
    }

    // Include tone and reason from the variant for metadata persistence
    if (variant.tone || variant.reason) {
        selections.tone = variant.tone
        selections.reason = variant.reason
    }

    return selections
}

/**
 * Transforms the new unified variantStates model to the legacy format for submission
 * This maintains backward compatibility with existing submission handlers
 */
export function transformVariantStatesForSubmission(
    variantStates: Record<
        number,
        {
            enabled: boolean
            selectedAction: { type: 'add' } | { type: 'replace'; targetIndex: number } | null
            selections: Partial<INotificationFieldSuggestions>
        }
    >,
    results: IEnhancedNotificationFieldSuggestions[],
    keywords: string[] | null,
    domain: DomainDto,
): {
    replaceVariants: Array<{
        variantIndex: number
        targetIndex: number
        selections: Partial<INotificationFieldSuggestions>
    }>
    addVariants: Partial<INotificationFieldSuggestions>[]
} {
    const replaceVariants: Array<{
        variantIndex: number
        targetIndex: number
        selections: Partial<INotificationFieldSuggestions>
    }> = []
    const addVariants: Partial<INotificationFieldSuggestions>[] = []

    Object.entries(variantStates).forEach(([index, state]) => {
        if (!state.enabled || !state.selectedAction) return

        const variantIndex = parseInt(index, 10)
        const variant = results[variantIndex]
        if (!variant) return

        // Build selections (same logic for both replace and add — respects user's checkbox choices)
        const selections: Partial<INotificationFieldSuggestions> = {
            ...state.selections,
        }
        if (keywords && keywords.length > 0 && canShowField(domain, 'keywords')) {
            selections.keywords = keywords
        }
        if (variant.tone || variant.reason) {
            selections.tone = variant.tone
            selections.reason = variant.reason
        }

        if (state.selectedAction.type === 'replace') {
            replaceVariants.push({
                variantIndex,
                targetIndex: state.selectedAction.targetIndex,
                selections,
            })
        } else {
            addVariants.push(selections)
        }
    })

    return { replaceVariants, addVariants }
}
